import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Link2, Plus, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { resetGiftCatalog } from "../lib/giftCatalog";
import { useAuth } from "../context/AuthContext";
import { useSeo } from "../lib/seo";
import { BUDGETS, RECIPIENTS } from "../data/presents";
import { Panel } from "../components/PageShell";
import { PopButton } from "../components/PopButton";
import { APPROVALS, EMPTY_GIFT, GiftForm, RISKS, type AdminGift } from "../components/admin/GiftForm";
import { SiteSettingsBox } from "../components/admin/SiteSettingsBox";

type ListResp = { items: AdminGift[]; total: number; page: number; page_size: number; counts: { active: number; archived: number; linked: number; needs_link: number }; networks: string[] };
type ImportResp = { created: number; updated: number; error_count: number; errors: { row: number; name: string; error: string }[] };

function Toolbar({ onAdd, onImported }: { onAdd: () => void; onImported: (r: ImportResp) => void }) {
  const file = useRef<HTMLInputElement>(null);
  const exportCsv = async () => {
    try {
      const r = await api.get("/admin/gifts/export.csv", { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = "dither-gift-catalogue.csv";
      a.click();
    } catch (e) { toast.error(apiError(e)); }
  };
  const importCsv = async (f?: File) => {
    if (!f) return;
    try {
      const { data } = await api.post<ImportResp>("/admin/gifts/import", { csv: await f.text() });
      onImported(data);
    } catch (e) { toast.error(apiError(e)); } finally { if (file.current) file.current.value = ""; }
  };
  return (
    <div className="flex flex-wrap gap-2">
      <PopButton tone="orange" size="sm" onClick={onAdd} data-testid="admin-gift-add-button"><Plus className="h-4 w-4" /> Add gift</PopButton>
      <PopButton tone="white" size="sm" onClick={() => file.current?.click()} data-testid="admin-gift-import-button"><Upload className="h-4 w-4" /> Import CSV</PopButton>
      <PopButton tone="white" size="sm" onClick={exportCsv} data-testid="admin-gift-export-button"><Download className="h-4 w-4" /> Export CSV</PopButton>
      <input ref={file} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => importCsv(e.target.files?.[0])} data-testid="admin-gift-import-input" />
    </div>
  );
}

const RISK_TONE = { green: "bg-[#35E07A] text-[#0B1F33]", amber: "bg-[#FFB020] text-[#3b1800]", red: "bg-[#C8102E] text-white" };
const SORTS = [["name", "Name A–Z"], ["commission_desc", "Commission: high → low"], ["commission_asc", "Commission: low → high"], ["network", "Network A–Z"],
  ["risk", "Risk: green → red"], ["approval", "Approval: approved first"], ["link", "Links first"], ["no_link", "No links first"], ["last_checked", "Last checked: oldest first"]];
const sel = "field !w-auto !py-2 !text-sm";
type F = { q: string; status: string; recipient: string; network: string; risk: string; approval: string; link: string; sort: string };

function Filters({ f, set, networks }: { f: F; set: (k: keyof F) => (v: string) => void; networks: string[] }) {
  const pick = (k: keyof F, testId: string, opts: (string[])[]) => (
    <select className={sel} value={f[k]} onChange={(e) => set(k)(e.target.value)} data-testid={testId}>{opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
  );
  return (
    <div className="flex flex-wrap gap-2" data-testid="admin-gift-filters">
      <input className="field !w-auto flex-1 !py-2 !text-sm" placeholder="Search name, retailer or id…" value={f.q} onChange={(e) => set("q")(e.target.value)} data-testid="admin-gift-search" />
      {pick("status", "admin-gift-status-filter", [["", "All statuses"], ["active", "Active"], ["archived", "Archived"]])}
      {pick("recipient", "admin-gift-recipient-filter", [["", "All recipients"], ...RECIPIENTS.map((r) => [r.id, r.label])])}
      {pick("network", "admin-gift-network-filter", [["", "All networks"], ...networks.map((n) => [n, n]), ["none", "No network"]])}
      {pick("risk", "admin-gift-risk-filter", [["", "Any risk"], ...RISKS.map((r) => [r.id, r.label]), ["none", "Risk not set"]])}
      {pick("approval", "admin-gift-approval-filter", [["", "Any approval"], ...APPROVALS.map((r) => [r.id, r.label]), ["none", "Approval not set"]])}
      {pick("link", "admin-gift-link-filter", [["", "Linked & needs link"], ["yes", "Linked (approved)"], ["no", "Needs link"]])}
      {pick("sort", "admin-gift-sort", SORTS)}
    </div>
  );
}

function GiftRow({ g, onEdit, onStatus, onDelete, checked, onCheck }: { g: AdminGift; onEdit: () => void; onStatus: () => void; onDelete: () => void; checked: boolean; onCheck: () => void }) {
  const archived = g.status === "archived";
  return (
    <li className={`flex flex-wrap items-center gap-2 rounded-xl border-2 bg-white px-3 py-2 ${archived ? "border-dashed border-[#F2D9B8] opacity-60" : "border-[#F2D9B8]"}`} data-testid={`admin-gift-row-${g.id}`}>
      <input type="checkbox" checked={checked} onChange={onCheck} className="h-4 w-4 accent-[#FF5D1F]" aria-label={`Select ${g.name}`} data-testid={`admin-gift-select-${g.id}`} />
      {g.image_url ? <img src={g.image_url} alt="" className="h-10 w-10 rounded-lg border object-contain" /> : <img src="/icons/gift.png" alt="" className="h-9 w-9 object-contain opacity-60" />}
      <div className="min-w-0 flex-1">
        <p className="truncate font-comic text-sm">{g.name}</p>
        <p className="truncate text-[11px] font-bold text-[#6b3a12]">
          {g.price != null ? `£${g.price.toFixed(2)}` : BUDGETS.find((b) => b.id === g.budget)?.label} · {g.recipients.join(", ")} · {g.vibes.join(", ")}{g.retailer && ` · ${g.retailer}`}
        </p>
      </div>
      <div className="flex items-center gap-1.5 text-[10px] font-extrabold uppercase">
        {g.linked
          ? <span className="flex items-center gap-1 rounded bg-[#E3F7EA] px-1.5 py-0.5 text-[#0E7A3E]" data-testid={`admin-gift-linked-${g.id}`}><Link2 className="h-3 w-3" /> Linked · {g.clicks || 0} clicks</span>
          : <span className="rounded border border-[#C8102E] bg-[#FFE0E0] px-1.5 py-0.5 text-[#C8102E]" title={g.tracking_url ? "Has a URL but approval isn't Approved" : "No affiliate URL yet"} data-testid={`admin-gift-needs-link-${g.id}`}>Needs link{g.tracking_url ? " · not approved" : ""}</span>}
        {g.commission_rate != null && <span className="rounded bg-[#E2C9FF] px-1.5 py-0.5" data-testid={`admin-gift-commission-${g.id}`}>{g.commission_rate}%</span>}
        {g.risk && <span className={`rounded px-1.5 py-0.5 ${RISK_TONE[g.risk]}`} data-testid={`admin-gift-risk-${g.id}`}>{g.risk}</span>}
        {g.approval && <span className="rounded bg-[#CDEBFF] px-1.5 py-0.5" data-testid={`admin-gift-approval-${g.id}`}>{g.approval}</span>}
        {g.last_checked && <span className="rounded bg-[#F1F1F1] px-1.5 py-0.5 normal-case" title="Last checked">✓ {g.last_checked}</span>}
        {g.availability === "out_of_stock" && <span className="rounded bg-[#FFE0E0] px-1.5 py-0.5 text-[#C8102E]">Out of stock</span>}
        <span className={`rounded px-1.5 py-0.5 ${archived ? "bg-[#eee]" : "bg-[#FFE14D]"}`}>{g.status}</span>
      </div>
      <div className="flex gap-1.5">
        <PopButton size="sm" tone="white" onClick={onEdit} data-testid={`admin-gift-edit-${g.id}`}>Edit</PopButton>
        <PopButton size="sm" tone="white" onClick={onStatus} data-testid={`admin-gift-status-${g.id}`}>{archived ? "Restore" : "Archive"}</PopButton>
        {archived && <PopButton size="sm" tone="white" onClick={onDelete} data-testid={`admin-gift-delete-${g.id}`}>Delete</PopButton>}
      </div>
    </li>
  );
}

function BulkBar({ pageIds, picked, setPicked, total, onAllMatching, onBulk }: { pageIds: string[]; picked: Set<string>; setPicked: (s: Set<string>) => void; total: number; onAllMatching: () => void; onBulk: (s: "active" | "archived") => void }) {
  const allOnPage = pageIds.length > 0 && pageIds.every((id) => picked.has(id));
  const togglePage = () => { const n = new Set(picked); pageIds.forEach((id) => (allOnPage ? n.delete(id) : n.add(id))); setPicked(n); };
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl border-2 border-[#F2D9B8] bg-[#FFFBF2] px-3 py-2 text-xs font-bold" data-testid="admin-bulk-bar">
      <label className="flex items-center gap-1.5"><input type="checkbox" checked={allOnPage} onChange={togglePage} className="h-4 w-4 accent-[#FF5D1F]" data-testid="admin-gift-select-page" /> Select this page</label>
      <button type="button" className="underline" onClick={onAllMatching} data-testid="admin-gift-select-all-matching">Select all {total} matching</button>
      <span data-testid="admin-gift-selected-count">{picked.size} selected</span>
      {picked.size > 0 && <>
        <PopButton size="sm" tone="white" onClick={() => onBulk("archived")} data-testid="admin-bulk-archive">Archive selected</PopButton>
        <PopButton size="sm" tone="white" onClick={() => onBulk("active")} data-testid="admin-bulk-restore">Restore selected</PopButton>
        <button type="button" className="underline" onClick={() => setPicked(new Set())} data-testid="admin-bulk-clear">Clear</button>
      </>}
    </div>
  );
}

export default function AdminGiftsPage() {
  const { user } = useAuth();
  useSeo({ title: "Gift catalogue admin | DITHER", description: "Manage the gift catalogue.", noindex: true });
  const isAdmin = !!user && user.role === "admin";
  const [filters, setFilters] = useState({ q: "", status: "", recipient: "", network: "", risk: "", approval: "", link: "", sort: "name" });
  const [page, setPage] = useState(1);
  const [data, setData] = useState<ListResp | null>(null);
  const [editing, setEditing] = useState<AdminGift | null>(null);
  const [report, setReport] = useState<ImportResp | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const setFilter = (k: keyof typeof filters) => (v: string) => { setFilters((f) => ({ ...f, [k]: v })); setPage(1); setPicked(new Set()); };
  const load = useCallback(() => {
    api.get<ListResp>("/admin/gifts", { params: { ...filters, page } }).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  }, [filters, page]);
  useEffect(() => { if (!isAdmin) return; const t = setTimeout(load, 250); return () => clearTimeout(t); }, [isAdmin, load]);
  const changed = () => { resetGiftCatalog(); load(); };
  const flip = (id: string) => setPicked((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const pickAllMatching = async () => {
    try { const { data: r } = await api.get<{ ids: string[] }>("/admin/gifts/ids", { params: filters }); setPicked(new Set(r.ids)); } catch (e) { toast.error(apiError(e)); }
  };
  const bulk = async (status: "active" | "archived") => {
    try {
      const { data: r } = await api.post<{ updated: number }>("/admin/gifts/bulk-status", { ids: Array.from(picked), status });
      toast.success(`${r.updated} gift${r.updated === 1 ? "" : "s"} ${status === "archived" ? "archived" : "restored"}`);
      setPicked(new Set()); changed();
    } catch (e) { toast.error(apiError(e)); }
  };
  const toggle = async (g: AdminGift) => {
    try { await api.patch(`/admin/gifts/${g.id}/status`, { status: g.status === "archived" ? "active" : "archived" }); changed(); } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (g: AdminGift) => {
    if (!window.confirm(`Delete "${g.name}" for good? This can't be undone.`)) return;
    try { await api.delete(`/admin/gifts/${g.id}`); toast.success("Gift deleted"); changed(); } catch (e) { toast.error(apiError(e)); }
  };

  if (user === null) return null;
  if (!isAdmin) return <div className="mx-auto max-w-3xl p-4"><Panel><p className="font-bold" data-testid="admin-gifts-forbidden">This area is for the site admin. <Link to="/auth" className="underline">Sign in</Link></p></Panel></div>;
  const pages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;
  return (
    <div className="mx-auto max-w-5xl space-y-2 px-3 py-4" data-testid="admin-gifts-page">
      <h1 className="ua-title text-3xl text-[#FF5D1F] sm:text-4xl">Gift catalogue</h1>
      <SiteSettingsBox />
      {data && <p className="text-sm font-bold text-[#6b3a12]" data-testid="admin-gift-counts">{data.counts.active} active · {data.counts.archived} archived · {data.counts.linked} linked · <span className="text-[#C8102E]">{data.counts.needs_link} active need a link</span></p>}
      <Toolbar onAdd={() => setEditing({ ...EMPTY_GIFT })} onImported={(r) => { setReport(r); changed(); toast.success(`Imported: ${r.created} added, ${r.updated} updated`); }} />
      <p className="text-[11px] font-bold text-[#8a5a2b]">CSV columns: id, name, description, image_url, price, recipients, budget, vibes, occasions, retailer, affiliate_network, tracking_url, availability, status, commission_rate, risk (green/amber/red), approval (applied/approved/rejected), last_checked (YYYY-MM-DD). Separate multiple values with | (e.g. parent|partner). Rows with an existing id are updated; others are added.</p>
      {report && (
        <Panel className="!p-3" >
          <div data-testid="admin-gift-import-report">
            <p className="font-bold">Import: {report.created} added · {report.updated} updated · {report.error_count} skipped</p>
            {report.errors.map((e) => <p key={e.row} className="text-xs font-semibold text-[#C8102E]">Row {e.row}{e.name && ` (${e.name})`}: {e.error}</p>)}
          </div>
        </Panel>
      )}
      <Filters f={filters} set={setFilter} networks={data?.networks || []} />
      <p className="text-xs font-bold text-[#6b3a12]" data-testid="admin-gift-total">{data ? `${data.total} gifts` : "Loading…"}</p>
      {data && <BulkBar pageIds={data.items.map((g) => g.id!)} picked={picked} setPicked={setPicked} total={data.total} onAllMatching={pickAllMatching} onBulk={bulk} />}
      <ul className="space-y-1.5" data-testid="admin-gift-list">
        {data?.items.map((g) => <GiftRow key={g.id} g={g} checked={picked.has(g.id!)} onCheck={() => flip(g.id!)} onEdit={() => setEditing(g)} onStatus={() => toggle(g)} onDelete={() => remove(g)} />)}
      </ul>
      {pages > 1 && (
        <div className="flex items-center gap-2">
          <PopButton size="sm" tone="white" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="admin-gift-prev">Prev</PopButton>
          <span className="text-xs font-bold">Page {page} of {pages}</span>
          <PopButton size="sm" tone="white" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="admin-gift-next">Next</PopButton>
        </div>
      )}
      {editing && <GiftForm gift={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); changed(); }} />}
    </div>
  );
}
